import config from "config";

import { FC, Fragment } from "react";
import dayjs from "dayjs";
// i18next
import { useTranslation } from "react-i18next";
// Hooks
import useFormattedDate from "app/hooks/useFormattedDate";
import useToggle from "app/hooks/useToggle";
import useMenu from "app/hooks/useMenu";
// Models
import IBillingInvoice from "app/models/BillingInvoice";
// Components
import { IconButton } from "app/components/Mui";
// MUI
import { Box, Divider, TableCell, TableRow } from "@mui/material";
// Icons
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
//
import InvoiceDetailsDrawer from "./InvoiceDetailsDrawer";
import InvoiceDiscountFormDialog from "./InvoiceDiscountFormDialog";
import InvoicePayFormDialog from "./InvoicePayFormDialog";
// Utilities
import { toNumberWithCommas } from "app/utilities/Utilities";

type Props = {
  invoice: IBillingInvoice;
}

const InvoicesListItem:FC<Props> = ({ invoice }) => {
  const { t } = useTranslation();

  const dueDate = useFormattedDate(invoice.dueDate);
  const periodStart = useFormattedDate(invoice.periodStart);
  const periodEnd = useFormattedDate(dayjs(invoice.periodEnd).subtract(1, 'day').toISOString());
  
  const dueDateIsInPast = invoice.status === 'sent' && invoice.dueDate
    ? dayjs(invoice.dueDate).isBefore(dayjs(), 'day')
    : false
  ;

  const statusLabels:Record<string, string> = {
    sent: t('labels.outstanding'),
    paid: t('labels.paid')
  }

  const { open, toggle } = useToggle();
  const { Menu, MenuItem, openMenu } = useMenu();

  const { open:openDiscountDialog, toggle:toggleDiscountDialog } = useToggle();
  const { open:openPayDialog, toggle:togglePayDialog } = useToggle();

  const billingPeriod = periodStart && periodEnd
    ? `${periodStart} - ${periodEnd}`
    : null;

  const openInQuickBooks = () => {
    window.open(`${config.quickBooksUrl}/invoice?txnId=${invoice.quickBooksId}`, '_blank', 'noreferrer');
  }

  return (
    <Fragment>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell component="th" scope="row" sx={{ py: 1, px: 2 }}>
          {invoice.invoiceNumber}
        </TableCell>
        <TableCell sx={{ py: 1, px: 2 }}>{billingPeriod}</TableCell>
        <TableCell sx={{ py: 1, px: 2 }}>
          {statusLabels[invoice.status]}
          {dueDateIsInPast ? (
            <Box component="span" color="red">{` (${t('labels.overdue')})`}</Box>
          ) : null}
        </TableCell>
        <TableCell sx={{ py: 1, px: 2 }}>{dueDate}</TableCell>
        <TableCell sx={{ py: 1, px: 2 }}>${toNumberWithCommas(invoice.amount)}</TableCell>
        <TableCell align="right" sx={{ py: 1, px: 2 }}>
          <IconButton
            name={`invoice-actions-${invoice.id}`}
            onClick={openMenu}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu>
            <MenuItem
              name={`Invoice details ${invoice.id}`}
              onClick={toggle}
            >{t('labels.details')}</MenuItem>
            {invoice.status !== 'draft' ? (
              <Fragment>
                <Divider />
                {invoice.quickBooksId ? (
                  <MenuItem
                    name={`Invoice details ${invoice.id}`}
                    onClick={openInQuickBooks}
                  >{t('buttons.openInQuickBooks')}</MenuItem>
                ) : null}
                {invoice.status === 'sent' ? ([
                  <MenuItem
                    key={`set-discount-${invoice.id}`}
                    name={`set-discount-${invoice.id}`}
                    onClick={toggleDiscountDialog}
                    >{t('buttons.setDiscount')}</MenuItem>,
                    <MenuItem
                      key={`mark-paid-${invoice.id}`}
                      name={`mark-paid-${invoice.id}`}
                      onClick={togglePayDialog}
                    >{t('buttons.markPaid')}</MenuItem>
                  ]) : null
                }
              </Fragment>
            ) : null}
          </Menu>
        </TableCell>
      </TableRow>

      <InvoiceDetailsDrawer
        open={open}
        onClose={toggle}
        invoice={invoice}
      />
      {/* Discount dialog */}
      {openDiscountDialog ? (
        <InvoiceDiscountFormDialog
          open={openDiscountDialog}
          onClose={toggleDiscountDialog}
          invoiceId={invoice.id}
          discountAmount={invoice.discountAmount || 0}
          amount={invoice.amount}
        />
      ) : null}
      {/* Pay dialog */}
      {openPayDialog ? (
        <InvoicePayFormDialog
          open={openPayDialog}
          onClose={togglePayDialog}
          invoiceId={invoice.id}
        />
      ) : null}
    </Fragment>
  )
}

export default InvoicesListItem;
